(() => {
  // Product recommendation quiz (sections/quiz.liquid).
  // Questions and rules come from the section's blocks as JSON. Answers are kept
  // per question id as an array of answer labels. At the end the visitor is sent
  // to the results page with the recommended handles in the URL.

  const TYPE_ORDER = ['Protein', 'Pre-workout', 'Hydration', 'Performance Boost', 'Greens'];
  const LOADING_MS = 1700;
  const KLAVIYO_REVISION = '2024-10-15';

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const lines = (s) => String(s || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const pipes = (s) => String(s || '').split('|').map((t) => t.trim()).filter(Boolean);

  // "Label | description | icon:name | exclusive | excludes:Other label"
  function parseOption(line) {
    const [label, ...rest] = pipes(line);
    const opt = { label, excludes: [] };
    rest.forEach((t) => {
      if (/^icon:/i.test(t)) opt.icon = t.slice(5).trim();
      else if (/^excludes:/i.test(t)) opt.excludes.push(t.slice(9).trim());
      else if (t.toLowerCase() === 'exclusive') opt.exclusive = true;
      else opt.description = t;
    });
    return opt;
  }

  // "question_id: Answer A | Answer B"
  function parseCondition(line) {
    const i = line.indexOf(':');
    if (i < 0) return null;
    const q = line.slice(0, i).trim();
    const any = pipes(line.slice(i + 1));
    return q && any.length ? { q, any } : null;
  }

  function parseConfig(raw) {
    const questions = raw.questions
      .filter((q) => q.active !== false && q.id && q.title)
      .map((q) => ({
        ...q,
        id: q.id.trim(),
        options: lines(q.options).map(parseOption),
        showIf: q.showIf ? parseCondition(q.showIf) : null,
      }))
      .filter((q) => q.options.length);
    const types = {};
    const products = {}; // handle -> { title, url, image, description }, for the Klaviyo event
    const rules = raw.rules
      .filter((r) => r.active !== false)
      .map((r) => {
        r.add.forEach((p) => { types[p.handle] = p.type; products[p.handle] = p; });
        return { name: r.name, addon: !!r.addon, reason: (r.reason || '').trim(), when: lines(r.when).map(parseCondition).filter(Boolean), add: r.add.map((p) => p.handle), remove: r.remove || [] };
      });
    return { settings: raw.settings, icons: raw.icons || {}, questions, rules, types, products };
  }

  function visibleQuestions(cfg, answers) {
    return cfg.questions.filter((q) => {
      if (!q.showIf) return true;
      const a = answers[q.showIf.q];
      if (!a || !a.length) return true; // the question it depends on has not been answered yet
      return a.some((v) => q.showIf.any.includes(v));
    });
  }

  function recommend(cfg, answers) {
    const has = (c) => (answers[c.q] || []).some((v) => c.any.includes(v));
    let picks = [];
    let extras = []; // suggested add-ons: relevant, but not part of the plan
    const reasons = {};
    cfg.rules.forEach((r) => {
      if (!r.when.every(has)) return;
      picks = picks.filter((h) => !r.remove.includes(h));
      extras = extras.filter((h) => !r.remove.includes(h));
      r.remove.forEach((h) => { delete reasons[h]; });
      r.add.forEach((h) => {
        if (r.addon) {
          if (picks.includes(h)) return;
          if (!extras.includes(h)) extras.push(h);
        } else {
          if (!picks.includes(h)) picks.push(h);
          // A plan rule outranks an earlier add-on suggestion, reason included.
          if (extras.includes(h)) { extras = extras.filter((x) => x !== h); delete reasons[h]; }
        }
        // The reason comes from the first matching rule that has one.
        if (r.reason && !reasons[h]) reasons[h] = r.reason;
      });
    });
    const rank = (h) => { const i = TYPE_ORDER.indexOf(cfg.types[h]); return i < 0 ? TYPE_ORDER.length : i; };
    const handles = picks.map((h, i) => [h, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map((x) => x[0]);
    return { handles, extras, reasons };
  }

  // goal / intensity / dietary / pregnancy are what the Digioh quiz sent and the old
  // results page reads. The rest feeds the new results page (sections/quiz-results.liquid).
  function resultsUrl(cfg, answers, handles, extras, reasons) {
    const p = new URLSearchParams(cfg.settings.resultsParams || '');
    const first = (id) => (answers[id] || [])[0] || '';
    const all = (id) => (answers[id] || []).filter((v) => v !== 'None' && v !== 'Other').join('|');
    p.set('products', handles.join(','));
    p.set('goal', first('goals'));
    p.set('intensity', first('intensity'));
    p.set('dietary', first('dietary'));
    p.set('pregnancy', first('pregnancy'));
    const extra = {
      goals: all('goals'),
      // The results page shows each goal with the quiz's own icon.
      goal_icons: (answers.goals || []).filter((v) => v !== 'None' && v !== 'Other').map((v) => {
        const q = cfg.questions.find((x) => x.id === 'goals');
        const o = q && q.options.find((x) => x.label === v);
        return (o && o.icon) || '';
      }).join('|'),
      diets: all('dietary'),
      other: all('other_goals'),
      frequency: first('frequency'),
      time: first('time_of_day'),
      more: extras.join(','),
      why: handles.concat(extras).filter((h) => reasons[h]).map((h) => `${h}::${reasons[h]}`).join('||'),
    };
    Object.keys(extra).forEach((k) => { if (extra[k]) p.set(k, extra[k]); });
    const base = cfg.settings.resultsUrl || '/pages/quiz-results';
    return base + (base.includes('?') ? '&' : '?') + p.toString();
  }

  // US numbers are accepted without a country code; anything else must start with +.
  function toE164(phone) {
    const raw = String(phone || '').trim();
    const digits = raw.replace(/\D/g, '');
    if (raw.startsWith('+') && digits.length >= 8 && digits.length <= 15) return '+' + digits;
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
    return '';
  }

  function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...data });
  }

  function klaviyoPost(path, company, data) {
    return fetch(`https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({ data }),
      keepalive: true,
    }).then((res) => { if (!res.ok) throw new Error(`klaviyo ${path} ${res.status}`); });
  }

  // Subscribes the email (and phone, for SMS) to the list, then records the quiz
  // event with the answers. Failures are swallowed: they must never block results.
  function sendToKlaviyo(cfg, state, handles, extras, reasons, url) {
    const k = cfg.settings.klaviyo || {};
    if (!k.company || !state.email) return Promise.resolve();
    const phone = toE164(state.phone);
    const answers = {};
    Object.keys(state.answers).forEach((id) => { answers[`quiz_${id}`] = state.answers[id]; });
    const resultsHref = new URL(url, window.location.origin).href;
    // quiz_results / results_url match what the Digioh quiz sent, so the existing
    // follow-up email template (top pick name, blurb, image, link) keeps working.
    const detail = (h) => { const p = cfg.products[h]; return p && { handle: p.handle, name: p.title, url: p.url, image_url: p.image, description: p.description, type: p.type, reason: reasons[h] || '' }; };
    const properties = {
      ...answers,
      quiz_name: 'Gainful Quiz',
      quiz_products: handles,
      quiz_results: handles.map(detail).filter(Boolean),
      quiz_addons: extras.map(detail).filter(Boolean),
      quiz_results_url: resultsHref,
      results_url: resultsHref,
    };
    const calls = [];
    if (k.list) {
      // The client subscriptions endpoint subscribes every channel it is given an
      // identifier for (email, and SMS when a phone number is included); it rejects
      // an explicit "subscriptions" field.
      calls.push(klaviyoPost('subscriptions', k.company, {
        type: 'subscription',
        attributes: {
          custom_source: k.source || 'Quiz',
          profile: { data: { type: 'profile', attributes: { email: state.email, ...(phone ? { phone_number: phone } : {}), properties } } },
        },
        relationships: { list: { data: { type: 'list', id: k.list } } },
      }));
    }
    if (k.metric) {
      calls.push(klaviyoPost('events', k.company, {
        type: 'event',
        attributes: {
          properties,
          metric: { data: { type: 'metric', attributes: { name: k.metric } } },
          profile: { data: { type: 'profile', attributes: { email: state.email, properties: answers } } },
        },
      }));
    }
    return Promise.all(calls.map((c) => c.catch((e) => console.warn('[quiz]', e))));
  }

  function init(root) {
    if (!root || root.dataset.quizInit) return;
    const configEl = root.querySelector('[data-quiz-config]');
    if (!configEl) return;
    let cfg;
    try { cfg = parseConfig(JSON.parse(configEl.textContent)); } catch (e) { console.error('[quiz] bad config', e); return; }
    root.dataset.quizInit = '1';

    const designMode = !!(window.Shopify && window.Shopify.designMode);
    const body = root.querySelector('[data-quiz-body]');
    const fill = root.querySelector('[data-quiz-fill]');
    const marker = root.querySelector('[data-quiz-marker]');
    const trackEl = root.querySelector('[data-quiz-track]');
    const backBtn = root.querySelector('[data-quiz-back]');
    const legal = root.querySelector('[data-quiz-legal]');
    const emailOn = cfg.settings.email.enabled;

    // Answers survive a reload within the tab, as long as the questions are unchanged.
    const storeKey = `gainful-quiz:${cfg.questions.map((q) => q.id).join(',')}`;
    const fresh = () => ({ answers: {}, idx: 0, phase: 'question', sel: [], email: '', phone: '', started: false, progress: 0 });
    let state = fresh();
    if (!designMode) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(storeKey) || 'null');
        if (saved && saved.answers) {
          state.answers = saved.answers;
          state.started = true;
          state.idx = Math.min(saved.idx || 0, Math.max(visibleQuestions(cfg, state.answers).length - 1, 0));
        }
      } catch (e) { /* storage unavailable */ }
    }
    const save = () => { try { sessionStorage.setItem(storeKey, JSON.stringify({ answers: state.answers, idx: state.idx })); } catch (e) { /* ignore */ } };
    const clearSaved = () => { try { sessionStorage.removeItem(storeKey); } catch (e) { /* ignore */ } };

    function setProgress(pct, done) {
      const from = state.progress;
      fill.style.width = `${pct}%`;
      marker.style.left = pct <= 0 ? '-48px' : `${pct}%`;
      marker.style.setProperty('--gq-lvl', pct / 100);
      trackEl.setAttribute('aria-valuenow', pct);
      const dir = pct > from ? 'is-fwd' : pct < from ? 'is-back' : done ? 'is-done' : '';
      marker.classList.remove('is-fwd', 'is-back', 'is-done');
      if (dir) { void marker.offsetWidth; marker.classList.add(dir); }
      state.progress = pct;
    }

    function render(focus) {
      const qs = visibleQuestions(cfg, state.answers);
      const total = qs.length + (emailOn ? 1 : 0);
      root.querySelector('.gq-bar')?.remove();

      if (state.phase === 'question') {
        const q = qs[state.idx];
        if (!q) { state.phase = emailOn ? 'email' : 'loading'; render(focus); if (state.phase === 'loading') finish(); return; }
        state.sel = [...(state.answers[q.id] || [])];
        const multi = q.type === 'multi';
        const one = q.options.length <= 3 && !q.options.some((o) => o.icon);
        body.innerHTML = `<section class="gq-step" data-quiz-step="${esc(q.id)}">
          <h1 class="gq-title" tabindex="-1">${esc(q.title)}</h1>
          ${q.subtitle ? `<p class="gq-sub">${esc(q.subtitle)}</p>` : ''}
          <div class="gq-opts${one ? ' is-one' : ''}" role="${multi ? 'group' : 'radiogroup'}" aria-label="${esc(q.title)}">
            ${q.options.map((o) => {
              const on = state.sel.includes(o.label);
              const icon = o.icon && cfg.icons[o.icon] ? `<span class="gq-opt-icon"><img src="${esc(cfg.icons[o.icon])}" alt="" width="26" height="26"></span>` : '';
              return `<button type="button" class="gq-opt${on ? ' is-on' : ''}" data-value="${esc(o.label)}" role="${multi ? 'checkbox' : 'radio'}" aria-checked="${on}">${icon}<span>${esc(o.label)}${o.description ? `<span class="gq-opt-desc">${esc(o.description)}</span>` : ''}</span></button>`;
            }).join('')}
          </div>
          ${multi ? '<p class="gq-note" role="status" data-quiz-note></p>' : ''}
          ${q.helper ? `<div class="gq-help">${esc(q.helper)}</div>` : ''}
        </section>`;
        if (multi) {
          root.insertAdjacentHTML('beforeend', `<div class="gq-bar"><button type="button" class="gq-btn" data-quiz-continue${state.sel.length ? '' : ' disabled'}>${esc(cfg.settings.continueLabel || 'Continue')}</button></div>`);
          root.querySelector('[data-quiz-continue]').addEventListener('click', () => { if (state.sel.length) advance(q); });
        }
        body.querySelectorAll('.gq-opt').forEach((btn) => btn.addEventListener('click', () => choose(q, btn)));
        backBtn.style.visibility = state.idx > 0 ? 'visible' : 'hidden';
        setProgress(Math.round((100 * state.idx) / total));
        track('quiz_step', { quiz_step: q.id, quiz_step_number: state.idx + 1 });
      } else if (state.phase === 'email') {
        const e = cfg.settings.email;
        body.innerHTML = `<section class="gq-step" data-quiz-step="email">
          <h1 class="gq-title" tabindex="-1">${esc(e.title)}</h1>
          <form class="gq-form" data-quiz-email novalidate>
            <label class="gq-sr" for="gq-email">Email</label>
            <input id="gq-email" type="email" name="email" placeholder="Email" autocomplete="email" required value="${esc(state.email)}">
            ${e.showPhone ? `<label class="gq-sr" for="gq-phone">Phone (optional)</label><input id="gq-phone" type="tel" name="phone" placeholder="Phone (optional)" autocomplete="tel" value="${esc(state.phone)}">` : ''}
            <p class="gq-note" role="alert" data-quiz-note hidden></p>
            <button class="gq-btn" type="submit">${esc(e.submitLabel)}</button>
            ${e.allowSkip ? `<div class="gq-or">Or</div><button class="gq-btn gq-btn--ghost" type="button" data-quiz-skip>${esc(e.skipLabel)}</button>` : ''}
          </form>
          <div class="gq-legal">${legal ? legal.innerHTML : ''}</div>
        </section>`;
        const form = body.querySelector('[data-quiz-email]');
        form.addEventListener('submit', (ev) => {
          ev.preventDefault();
          const note = form.querySelector('[data-quiz-note]');
          const email = form.email.value.trim();
          const phone = form.phone ? form.phone.value.trim() : '';
          let error = '';
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) error = 'Enter a valid email address.';
          else if (phone && !toE164(phone)) error = 'Enter a valid phone number, or leave it empty.';
          note.textContent = error;
          note.hidden = !error;
          if (error) { (error.includes('email') ? form.email : form.phone).focus(); return; }
          state.email = email;
          state.phone = phone;
          track('quiz_email', { quiz_email_captured: true, quiz_phone_captured: !!phone });
          state.phase = 'loading';
          render(true);
          finish();
        });
        form.querySelector('[data-quiz-skip]')?.addEventListener('click', () => {
          state.email = '';
          state.phone = '';
          track('quiz_email', { quiz_email_captured: false, quiz_phone_captured: false });
          state.phase = 'loading';
          render(true);
          finish();
        });
        backBtn.style.visibility = 'visible';
        setProgress(100, true);
        track('quiz_step', { quiz_step: 'email', quiz_step_number: total });
      } else {
        body.innerHTML = `<section class="gq-step gq-load" data-quiz-step="loading">
          <h1 class="gq-title" tabindex="-1" role="status">${esc(cfg.settings.loadingText)}</h1>
          <div class="gq-load-bar"><i></i></div>
        </section>`;
        backBtn.style.visibility = 'hidden';
        setProgress(100, true);
      }
      if (focus) body.querySelector('.gq-title')?.focus({ preventScroll: true });
      if (focus) window.scrollTo({ top: 0 });
    }

    function choose(q, btn) {
      const v = btn.dataset.value;
      if (q.type !== 'multi') {
        state.sel = [v];
        body.querySelectorAll('.gq-opt').forEach((b) => { const on = b === btn; b.classList.toggle('is-on', on); b.setAttribute('aria-checked', on); b.disabled = true; });
        setTimeout(() => advance(q), 180);
        return;
      }
      const note = body.querySelector('[data-quiz-note]');
      if (note) note.textContent = '';
      const optOf = (label) => q.options.find((o) => o.label === label);
      const isExclusive = (label) => !!optOf(label)?.exclusive;
      // Two answers that rule each other out (Gain weight / Lose weight): picking one drops the other.
      const conflicts = (a, b) => (optOf(a)?.excludes || []).includes(b) || (optOf(b)?.excludes || []).includes(a);
      if (state.sel.includes(v)) state.sel = state.sel.filter((x) => x !== v);
      else if (isExclusive(v)) state.sel = [v];
      else {
        const dropped = state.sel.filter((x) => conflicts(x, v));
        state.sel = state.sel.filter((x) => !isExclusive(x) && !conflicts(x, v));
        if (q.max && state.sel.length >= q.max) { if (note) note.textContent = `Choose up to ${q.max}`; return; }
        state.sel.push(v);
        if (dropped.length && note) note.textContent = `${v} and ${dropped.join(', ')} can't both be picked, so ${dropped.join(', ')} was removed.`;
      }
      body.querySelectorAll('.gq-opt').forEach((b) => { const on = state.sel.includes(b.dataset.value); b.classList.toggle('is-on', on); b.setAttribute('aria-checked', on); });
      const cont = root.querySelector('[data-quiz-continue]');
      if (cont) cont.disabled = !state.sel.length;
    }

    function advance(q) {
      if (!state.started) { state.started = true; track('quiz_start', {}); }
      state.answers[q.id] = [...state.sel];
      // Drop answers to questions that are no longer shown (their condition stopped matching).
      const now = visibleQuestions(cfg, state.answers);
      Object.keys(state.answers).forEach((id) => { if (!now.some((x) => x.id === id)) delete state.answers[id]; });
      track('quiz_answer', { quiz_step: q.id, quiz_answer: state.answers[q.id].join(' | ') });
      state.idx += 1;
      save();
      if (state.idx >= now.length) state.phase = emailOn ? 'email' : 'loading';
      render(true);
      if (state.phase === 'loading') finish();
    }

    function finish() {
      const { handles, extras, reasons } = recommend(cfg, state.answers);
      const url = resultsUrl(cfg, state.answers, handles, extras, reasons);
      track('quiz_complete', { quiz_products: handles.join(','), quiz_email_captured: !!state.email });
      const wait = new Promise((resolve) => setTimeout(resolve, LOADING_MS));
      // Give Klaviyo a moment, but never hold the visitor for more than a few seconds.
      const sent = Promise.race([sendToKlaviyo(cfg, state, handles, extras, reasons, url), new Promise((resolve) => setTimeout(resolve, 4000))]);
      Promise.all([wait, sent]).then(() => {
        if (designMode) {
          body.innerHTML = `<section class="gq-step gq-load"><h1 class="gq-title">Quiz finished</h1><p class="gq-editor-note">Visitors are sent to:<br>${esc(url)}</p><button type="button" class="gq-btn gq-btn--ghost" data-quiz-restart>Start over</button></section>`;
          body.querySelector('[data-quiz-restart]').addEventListener('click', restart);
          return;
        }
        clearSaved();
        window.location.assign(url);
      });
    }

    function restart() {
      clearSaved();
      state = { ...fresh(), progress: state.progress };
      render(true);
    }

    backBtn.addEventListener('click', () => {
      if (state.phase === 'email') { state.phase = 'question'; state.idx = visibleQuestions(cfg, state.answers).length - 1; }
      else if (state.idx > 0) state.idx -= 1;
      render(true);
    });

    // Coming back from the results page with the browser's back button restores the
    // page frozen on the loading step. Start again from the first question instead.
    window.addEventListener('pageshow', (e) => { if (e.persisted && state.phase === 'loading') restart(); });

    // Theme editor: selecting a question block jumps to that question.
    root.addEventListener('quiz:goto', (e) => {
      const i = visibleQuestions(cfg, state.answers).findIndex((q) => q.block === e.detail);
      if (i < 0) return;
      state.phase = 'question';
      state.idx = i;
      render(false);
    });

    render(false);
  }

  const boot = () => document.querySelectorAll('[data-section-type="quiz"]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target && e.target.querySelector('[data-section-type="quiz"]');
    if (root) init(root);
  });

  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target && e.target.closest('[data-section-type="quiz"]');
    if (root) root.dispatchEvent(new CustomEvent('quiz:goto', { detail: e.detail.blockId }));
  });
})();
